/**
 * Widget 展示容器匹配规则
 *
 * 表盘编辑工具中，设计师通过 Slot（容器）放置 Widget 组件。
 * App 端需要根据 Widget 的实际画布尺寸（W×H），自动匹配合适的展示容器。
 *
 * 匹配变量：
 *   L = max(W, H)  —— Widget 最大边长
 *   R = W / H      —— Widget 宽高比
 *
 * 5 种展示容器：
 *   圆形 96×96   —— 小图标类（快捷入口、小数据）
 *   1/4弧 170×170  —— 中方形（近似正方形数据组件）
 *   1/2弧 170×232  —— 竖向矩形（纵向数据展示）
 *   卡片 384×170  —— 横向大卡
 *   胶囊 384×100  —— 横向窄条
 */

import { useWatchfaceStore } from '@/store/use-watchface-store';

/** Widget 展示容器类型 */
export type WidgetContainerType =
  | 'circle'      // 圆形 96×96
  | 'quarterArc'  // 1/4弧 170×170
  | 'halfArc'     // 1/2弧 170×232
  | 'card'        // 卡片 384×170
  | 'capsule';    // 胶囊 384×100

/** 展示容器规格定义 */
export interface WidgetContainerSpec {
  /** 容器类型 */
  type: WidgetContainerType;
  /** 容器名称 */
  name: string;
  /** 容器规格尺寸 */
  size: { width: number; height: number };
  /** 适用场景描述 */
  scene: string;
  /**
   * 匹配方法，根据 Widget 尺寸判断是否命中该容器
   * @param L 最大边长 max(W, H)
   * @param R 宽高比 W / H
   * @param H Widget 高度
   * @returns 是否匹配
   */
  match: (L: number, R: number, H: number) => boolean;
}

/** 按分辨率区分的规则配置 */
export interface WidgetDisplayContainerRuleSet {
  /** 规则描述 */
  description: string;
  /** 适用的分辨率列表，格式如 '480x480'、'336x480' */
  resolutions: string[];
  /** 展示容器定义，按匹配优先级从高到低排列 */
  containers: WidgetContainerSpec[];
}

/**
 * Widget 展示容器匹配规则集合
 * 按分辨率区分，分辨率字段可自行填写
 */
export const WidgetDisplayContainerRules: WidgetDisplayContainerRuleSet[] = [
  {
    description: '圆形表盘',
    resolutions: ['480x480'],
    containers: [
      {
        type: 'circle',
        name: '圆形',
        size: { width: 96, height: 96 },
        scene: '小图标类（快捷入口、小数据）',
        // L <= 100；或 0.9 <= R <= 1.1 且 L <= 144
        match: (L, R) => L <= 100 || (R >= 0.9 && R <= 1.1 && L <= 144)
      },
      {
        type: 'quarterArc',
        name: '1/4弧',
        size: { width: 170, height: 170 },
        scene: '中方形（近似正方形数据组件）',
        // 0.7 < R <= 1.3 且 L > 144
        match: (L, R) => R > 0.7 && R <= 1.3 && L > 144
      },
      {
        type: 'halfArc',
        name: '1/2弧',
        size: { width: 170, height: 232 },
        scene: '竖向矩形（纵向数据展示）',
        // 0 < R <= 0.7
        match: (_L, R) => R > 0 && R <= 0.7
      },
      {
        type: 'card',
        name: '卡片',
        size: { width: 384, height: 170 },
        scene: '横向大卡（表盘小组件暂不支持app编辑）',
        // R > 1.3 且 H > 120
        match: (_L, R, H) => R > 1.3 && H > 120
      },
      {
        type: 'capsule',
        name: '胶囊',
        size: { width: 384, height: 100 },
        scene: '横向窄条',
        // R > 1.3 且 H <= 120
        match: (_L, R, H) => R > 1.3 && H <= 120
      }
    ]
  }
];

/**
 * 根据 Widget 尺寸匹配展示容器类型
 * 自动从 watchfaceStore 获取当前表盘分辨率
 * @param width Widget 宽度
 * @param height Widget 高度
 * @returns 匹配到的容器类型，未匹配返回 null
 */
export function matchWidgetContainer(
  width: number,
  height: number
): WidgetContainerType | null {
  const watchfaceStore = useWatchfaceStore();
  const { width: deviceWidth, height: deviceHeight } = watchfaceStore.size;
  const resolution = `${deviceWidth}x${deviceHeight}`;

  const L = Math.max(width, height);
  const R = width / height;
  const H = height;

  // 查找适用当前分辨率的规则集
  const ruleSet = WidgetDisplayContainerRules.find((rs) =>
    rs.resolutions.includes(resolution)
  );
  if (!ruleSet) return null;

  // 按容器优先级逐个匹配，命中第一个即返回
  for (const container of ruleSet.containers) {
    if (container.match(L, R, H)) {
      return container.type;
    }
  }

  return null;
}
